import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchGameState } from '../services/api';
import { debugLog } from '../utils/debugLogger';
import { decodeBase64 } from '../utils/base64';
import { GAME_STATE_IMPORTED_EVENT } from '../constants/events';

const decodeBase64String = (value: string | null) => {
  if (!value) return null;
  try {
    return decodeBase64(value);
  } catch (error) {
    console.error('Failed to decode base64 payload', error);
    return null;
  }
};

const parseSnapshot = (value: string | null) => {
  const decoded = decodeBase64String(value);
  if (!decoded) return null;
  try {
    return JSON.parse(decoded);
  } catch (error) {
    console.error('Failed to parse snapshot JSON', error);
    return null;
  }
};

const storeGameState = (gameState: any) => {
  if (!gameState) return;
  localStorage.setItem('gameState', JSON.stringify(gameState));
  if (gameState.playerName) {
    localStorage.setItem('playerName', gameState.playerName);
  }
};

export default function ImportGameState() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const importGameState = async () => {
      const params = new URLSearchParams(location.search);
      const snapshotParam = params.get('state');
      const pidParam = params.get('pid');
      const legacyParam = params.get('data');

      let resolvedPlayerId: string | null = null;
      let hadSnapshot = false;

      if (snapshotParam) {
        const snapshot = parseSnapshot(snapshotParam);
        if (!snapshot) {
          alert('Invalid save data provided.');
          navigate('/', { replace: true });
          return;
        }

        resolvedPlayerId =
          pidParam ||
          snapshot?.playerId ||
          snapshot?.PlayerId ||
          snapshot?.playerID ||
          null;

        if (resolvedPlayerId) {
          localStorage.setItem('playerId', resolvedPlayerId);
        }

        storeGameState(snapshot);
        hadSnapshot = true;
        debugLog('[ImportGameState] Applied snapshot payload from link');
      }

      if (!resolvedPlayerId && pidParam) {
        resolvedPlayerId = pidParam;
        localStorage.setItem('playerId', pidParam);
      }

      if (!resolvedPlayerId && legacyParam) {
        const decoded = decodeBase64String(legacyParam);
        const playerId = decoded ?? atob(legacyParam);
        if (playerId) {
          resolvedPlayerId = playerId;
          localStorage.setItem('playerId', playerId);
        }
      }

      if (!resolvedPlayerId) {
        alert('No player ID found in link.');
        navigate('/', { replace: true });
        return;
      }

      try {
        const serverState = await fetchGameState(resolvedPlayerId);
        storeGameState(serverState);
        debugLog('[ImportGameState] Loaded server state after import');
      } catch (error) {
        console.error('Error fetching latest game state:', error);
        if (!hadSnapshot) {
          alert('Error importing game state: Could not retrieve save data.');
          navigate('/', { replace: true });
          return;
        }
      }

      window.dispatchEvent(new Event(GAME_STATE_IMPORTED_EVENT));
      navigate('/', { replace: true });
    };

    importGameState();
  }, [location, navigate]);

  return null;
}
